<template>
  <div class="resources-index">
    <div class="resources-index__main-content">
      <div class="resources-filter__header-wrapper js-filter-header-wrapper">
        <ResourcesFilterHeader
          :filters="filters"
          :selectedFilters="selectedFilters"
          :totalResults="totalResults"
          :searchTerm="searchTerm"
          :heading="cmsPage.heading"
        />
      </div>
      <div class="resources-filter__option-categories-wrapper">
        <ResourcesFilterOptions
          :filterOptionsCategories="filtersByCategory"
          :selectedFilters="selectedFilters"
          :searchTerm="searchTerm"
          :sampleTypes="cmsPage.sampleTypes"
          :selectedSampleType="cmsPage.sampleTypeSlug"
        />
      </div>
      <span class="resources-index__results-grid-container">
        <ResultsGrid
          :results="this.results"
          :error="error"
          :api-call-made="apiCallMade"
          :current-page="Number(page)"
          :total-results="totalResults"
          :per-page="24"
          :no-results-message="cmsPage.noResults"
          :no-results-redirect="cmsPage.noResultsSlug"
          :no-results-redirect-text="cmsPage.noResultsRedirectText"
          :no-results-open-chat="cmsPage.noResultsOpenChat"
          :total-pages="totalPages"
          :loading-data="loadingData"
          :selected-sample-type="cmsPage.sampleTypeSlug"
        />
        <div class="resources-index__loading-container">
          <div v-if="loadingData" class="loading"></div>
        </div>
      </span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import queryString from 'query-string';

import ResultsGrid from './ResultsGrid';
import ResourcesFilterHeader from './ResourcesFilterHeader';
import ResourcesFilterOptions from './ResourcesFilterOptions';

import { waitForElement } from '@Helpers/helpers.js';

import {
  eventBus,
  CLEAR_FILTERS,
  UPDATE_FILTERS,
  UPDATE_SEARCH_TERM,
  UPDATE_SELECTED_SAMPLE_TYPE,
} from '../shared/event-bus';

import { fireAdobeAnalyticsEvent } from '../../helpers/adobe-analytics';

// the various string keys that we need to send to Libris SEACH API
const queryKeys = {
  QUERY_SEARCH_TERM: 'searchTerm',
  QUERY_FILTERS: 'tag_names',
  QUERY_CURRENT_PAGE: 'page',
};

// a mapping of the query strings to the state object properties
const queryToStateMap = {
  [queryKeys.QUERY_SEARCH_TERM]: 'searchTerm',
  [queryKeys.QUERY_FILTERS]: 'selectedFilters',
  [queryKeys.QUERY_CURRENT_PAGE]: 'page',
};

const perPage = 24;

// strip out the string mainpulation for the url into a function
// if you are appending to the a url that already has a query string '?'
// then do one string manipulation
// otherwise to just add
// Taken from Libris Search
const newUrlCreator = appendMode => (stringAcc, obj, key) => {
  const value = encodeURIComponent(obj[key]);
  if ((key == 'searchTerm' && obj[key] != '') || key == 'page') {
    stringAcc = appendMode
      ? `${stringAcc}&${key}=${value}`
      : `?${key}=${value}`;
  } else {
    stringAcc = !!value
      ? appendMode
        ? `${stringAcc}&${key}=${value}`
        : `?${key}=${value}`
      : stringAcc;
  }

  return stringAcc;
};

export default {
  name: 'ResourcesIndex',
  components: {
    ResultsGrid,
    ResourcesFilterHeader,
    ResourcesFilterOptions,
  },
  props: ['contentJson', 'filterOptionsCategories'],
  data() {
    return {
      apiCallMade: true,
      error: null,
      lastPageLoaded: false,
      loadingData: false,
      page: 1,
      results: [],
      searchTerm: '',
      searchTermLengthThreshold: 2,
      selectedFilters: [],
      totalPages: null,
      totalResults: 0,
      debounceTimeout: null,
    };
  },
  computed: {
    content() {
      if (this.contentJson) return JSON.parse(this.contentJson);

      return {};
    },
    cmsPage() {
      return this.content.page_content;
    },
    filtersByCategory() {
      if (this.filterOptionsCategories) {
        return JSON.parse(this.filterOptionsCategories);
      }

      return [];
    },
    filters() {
      let result = {};

      this.filtersByCategory.forEach(category => {
        category.options.forEach(option => {
          result[option.value] = option.humanizedOption;
        });
      });

      return result;
    },
    optionsSorting() {
      let result = {};

      this.filtersByCategory.forEach((category, catIndex) => {
        category.options.forEach((option, optIndex) => {
          result[option.value] = `${catIndex}__${optIndex}`;
        });
      });

      return result;
    },
  },
  async mounted() {
    window.onpopstate = async () => {
      await this.handleSearchTerms();
      await this.openDefaultCategories();
      await this.updateFilterInputs();
      await this.sortSelectedFilters();

      // Always request resources last unless something requires results
      await this.requestResources();
    };

    await this.handleSearchTerms();
    await this.openDefaultCategories();
    await this.updateFilterInputs();
    await this.sortSelectedFilters();

    // Always request resources last unless something requires results
    await this.requestResources();

    // Scrolling does two things:
    //   changes header / options elements from static to fixed
    //   loads the next page
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    /**
     * Vue works with the concept of Props down and Events up.
     * Rather than set up a bunch of event listeners and listening all the way
     * through the component tree it is a pub/sub system that allows us to emit
     * events and have components change based on the events they subscribe to.
     * read more here: https://alligator.io/vuejs/global-event-bus/
     */
    eventBus.$on(CLEAR_FILTERS, () => {
      this.selectedFilters.forEach(selectedFilter => {
        const selector = `.js-resources-filter-option[data-value="${selectedFilter}"] input[type=checkbox]`;
        document.querySelector(selector).checked = false;
      });

      this.selectedFilters = [];
      this.updateUrl();
    });

    eventBus.$on(UPDATE_FILTERS, data => {
      if (data.checked) {
        this.selectedFilters.push(data.option?.value);
        this.sortSelectedFilters();
      } else {
        const index = this.selectedFilters.indexOf(data.option?.value);
        if (index !== -1) this.selectedFilters.splice(index, 1);
      }

      // ANALYTICS
      const clickAction = data.checked ? 'Select' : 'DeSelect';
      const filtersEventString = `Scroll Page|Filter ${data.category?.humanizedCategory}|${clickAction} ${data.option?.humanizedOption}`;
      this.sendAdobeAnalyticsEvent(filtersEventString);

      if (!data.skipFetch) this.updateUrl();
    });

    eventBus.$on(UPDATE_SEARCH_TERM, data => {
      if (data.length >= this.searchTermLengthThreshold || data.length === 0) {
        this.searchTerm = data;
        eventBus.clearAllFilters();

        if (!!this.searchTerm.length) {
          clearTimeout(this.debounceTimeout); // Clear the previous timeout

          this.debounceTimeout = setTimeout(() => {
            const resultsFound = !!this.results.length;
            const eventData = {
              type: 'tool',
              detail: {
                search: {
                  term: this.searchTerm,
                  results: resultsFound,
                  toolName: 'Scroll Page',
                },
              },
            };
            fireAdobeAnalyticsEvent(eventData);
          }, 500);
        }
      }
    });

    eventBus.$on(UPDATE_SELECTED_SAMPLE_TYPE, data => {
      const sampleTypeEventString = `Scroll Page|Select Material|${data.sampleType?.filterOptionText}`;
      this.sendAdobeAnalyticsEvent(sampleTypeEventString);
      this.cmsPage.sampleTypeSlug = data.sampleType?.slug;

      this.updateUrl();
    });
  },
  methods: {
    requestResources() {
      this.loadingData = true;

      const queryParams = location.search,
        separator = queryParams.length > 0 ? '&' : '?',
        sampleTypeSlug = this.cmsPage.sampleTypeSlug
          ? `/${this.cmsPage.sampleTypeSlug}`
          : '';

      return axios
        .get(
          `${this.cmsPage.resultsPath}${sampleTypeSlug}.json${queryParams}${separator}page=${this.page}`
        )
        .then(({ data }) => {
          this.totalPages = data.total_pages;
          this.totalResults = data.totalResults;
          this.lastPageLoaded =
            this.page >= this.totalPages || this.totalPages === 0;

          if (data.unavailableOptions) {
            data.unavailableOptions.forEach(unavailableOption => {
              const index = this.selectedFilters.indexOf(unavailableOption);
              if (index !== -1) this.selectedFilters.splice(index, 1);
            });

            this.updateUrl(true);
          }

          this.filterOptionsCategories = JSON.stringify(data.categories);

          data.results.forEach(result => {
            if (result.parentType === 'GraphicPattern') {
              // Render all graphic patterns in all selected colors
              data.colors.forEach(color => {
                let colorResult = Object.assign({}, result);

                colorResult.colorName = color.name;
                colorResult.colorHexCode = color.hexCode;
                colorResult.isLightText = color.isLightText;

                this.results.push(colorResult);
              });
            } else {
              this.results.push(result);
            }
          });

          this.loadingData = false;

          if (
            this.page == 1 &&
            !window.useMobileLayout &&
            !window.useTabletLayout
          ) {
            // new results should have the scroll position reset for desktop
            window.scrollTo(0, 0);

            // initially load two pages worth of results if desktop width
            this.page++;
            this.requestResources();
          }
        });
    },

    handleSearchTerms() {
      const parsed = queryString.parse(location.search);

      // loop over the query params that came from the URL
      for (let [key, value] of Object.entries(parsed)) {
        // first check to see if the key val pair is valid for our app
        if (this.isValidQueryString(value, key)) {
          // set state based on the valid query string
          if (
            key === queryKeys.QUERY_SEARCH_TERM ||
            key === queryKeys.QUERY_CURRENT_PAGE
          ) {
            this[key] = value;
          } else if (key === queryKeys.QUERY_FILTERS) {
            const newState = value.split(/[,\|]/);
            const [firstKey, secondKey] = queryToStateMap[key].split('.');

            if (newState) {
              if (!secondKey) {
                this[firstKey] = newState;
              } else {
                this[firstKey][secondKey] = newState;
              }
            }
          }
        }
      }
    },
    updateFilterInputs() {
      this.selectedFilters.forEach(async optionValue => {
        const selector = `.js-resources-filter-option[data-value="${optionValue}"] input[type=checkbox]`;
        const element = await waitForElement(selector);
        element.checked = true;
      });
    },
    isValidQueryString(obj, key) {
      if (!obj) {
        return false;
      }

      // make sure the user passed in a string and that the key that was passed was a valid key
      return (
        typeof obj === 'string' &&
        Object.values(queryKeys).some(val => val == key)
      );
    },
    updateUrl(skipRequest = false) {
      const { searchTerm, filtersByCategory, selectedFilters } = this,
        tags = filtersByCategory.reduce(
          (arrayAccumulator, category, categoryIndex) => {
            const categorySelectedOptions = category.options.filter(option => {
              return (
                selectedFilters.indexOf(option.value) > -1 &&
                !arrayAccumulator.some(value =>
                  value.match(new RegExp(`(^|[,|])${option.value}([,|]|$)`))
                )
              );
            });

            if (!categorySelectedOptions.length) return arrayAccumulator;

            const categorySelectedOptionValues = categorySelectedOptions.map(
              option => option.value
            );
            const categoryString = categorySelectedOptionValues.join(
              category.inclusive ? '|' : ','
            );

            arrayAccumulator.push(categoryString);

            return arrayAccumulator;
          },
          []
        );

      // create new query params based off of our App state
      let params = {};
      if (searchTerm) params.searchTerm = searchTerm;
      if (tags.length > 0) params.tag_names = tags;

      const newQueryParams = Object.keys(params).reduce((accumulator, val) => {
        const regex = /\?/;
        const modeAware = newUrlCreator(Boolean(accumulator.match(regex)));
        accumulator = modeAware(accumulator, params, val);
        return accumulator;
      }, '');

      const pathParts = window.location.pathname.match(
          /(\/(landing))?\/(\w+)(\/([\w-]+))?/
        ),
        currentSlug = pathParts[5],
        landingPrefix = pathParts[2];

      let newPath = pathParts[3];

      if (currentSlug && !this.cmsPage.sampleTypeSlug) {
        newPath = newPath + '/' + currentSlug;
      } else if (this.cmsPage.sampleTypeSlug) {
        newPath = newPath + '/' + this.cmsPage.sampleTypeSlug;
      }

      if (landingPrefix) newPath = landingPrefix + '/' + newPath;

      const newUrl = newPath + newQueryParams;

      history.replaceState({}, 'newUrl', '/' + newUrl);

      if (!skipRequest) {
        const requestIntervalID = setInterval(() => {
          if (this.loadingData) return;

          clearInterval(requestIntervalID);

          this.resetResults();
          this.requestResources();
        }, 50);
      }
    },
    handleScroll() {
      if (
        this.loadingData ||
        this.lastPageLoaded ||
        window.innerHeight + window.pageYOffset <
          document.body.offsetHeight - 1250
      )
        return;

      this.page++;
      this.requestResources();
    },
    resetResults() {
      this.results = [];

      this.totalResults = 0;
      this.page = 1;
    },
    sortSelectedFilters() {
      this.selectedFilters.sort((aOptionID, bOptionID) => {
        const [aCategory, aOption] = this.optionsSorting[aOptionID]
            .split('__')
            .map(numberString => {
              return Number(numberString);
            }),
          [bCategory, bOption] = this.optionsSorting[bOptionID]
            .split('__')
            .map(numberString => {
              return Number(numberString);
            });

        if (aCategory < bCategory) return -1;
        if (aCategory === bCategory && aOption < bOption) return -1;

        return 1;
      });
    },
    openDefaultCategories() {
      if (this.selectedFilters.length > 0) {
        eventBus.toggleCategories();
      }

      // Array.prototype.some loops through the array the same way as forEach,
      // except it allows to effectively break out of the loop by returning true
      // in the callback. Not really the way the function is intended to be
      // used, but it's nice to use for a slight optimization.
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
      this.filtersByCategory.some((category, index) => {
        if (index < 2) {
          // Always open first two categories
          document
            .querySelector(
              `.js-category-options-toggle[data-category="${category.id}"]`
            )
            .click();
        } else if (this.selectedFilters.length > 0) {
          // Open categories that have options selected
          const categoryOptionValues = category.options.map(option => {
              return option.value;
            }),
            selectedOptions = this.selectedFilters.filter(
              selectedOptionValue => {
                return categoryOptionValues.indexOf(selectedOptionValue) !== -1;
              }
            );

          if (selectedOptions.length > 0) {
            document
              .querySelector(
                `.js-category-options-toggle[data-category="${category.id}"]`
              )
              .click();
          }
        } else {
          // If we are past the second category, and there are no selected
          // filters, then we are done.
          return true;
        }
      });
    },
    sendAdobeAnalyticsEvent(eventStr) {
      const eventData = {
        type: 'tool',
        detail: {
          eVar35: eventStr,
        },
      };

      fireAdobeAnalyticsEvent(eventData);
    },
  },
};
</script>
